import { AfterViewInit, Component, Inject, Injector, OnDestroy, OnInit, Optional, ChangeDetectionStrategy } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialog, MatDialogRef } from '@angular/material/dialog';
import { Observable, of, Subscription } from 'rxjs';
import { JobDictionaryConfigDialogComponent } from 'src/app/modals/job-dictionary-config-dialog/job-dictionary-config-dialog.component';
import { JobDictionaryDialogComponent } from 'src/app/modals/job-dictionary-dialog/job-dictionary-dialog.component';
import { JobDictionaryHistoryDialogComponent } from 'src/app/modals/job-dictionary-history-dialog/job-dictionary-history-dialog.component';
import { JobDictionary } from 'src/app/models/jobDictionary.model';
import { Lookup } from 'src/app/models/lookup.model';
import { PermissionFlags } from 'src/app/enums/permissionFlags.enum';
import { AuthService } from 'src/app/services';
import { CommonService } from 'src/app/services/common.service';
import { DateFormatService } from 'src/app/services/dateFormat.service';
import { GridViewService } from 'src/app/services/grid-view.service';
import { JobDictionaryService } from 'src/app/services/job-dictionary.service';
import { ToastService } from 'src/app/services/toast.service';
import { TranslateService } from 'src/app/services/translate.service';
import { UserService } from 'src/app/services/user.service';
import { RolePermissionObjects } from 'src/app/shared/constants/rolePermissionObjects.constants';
import { workforceRouteConfig } from 'src/app/shared/constants/workforceRouteConfig.constants';
import { WorkforceTabsService } from 'src/app/services/workforce-tabs.service';
import { DeleteDialog } from 'src/app/shared/delete-dialog/delete-dialog.component';
import { IFormDialogData } from 'src/app/shared/form/form.component';
import { escapeHtml } from 'src/app/shared/helpers/html-escape.helper';
import nameOf from 'src/app/shared/form/nameOf.helper';
import { ColumnConfig } from 'src/app/shared/grid/core-grid/columnConfig.model';
import { CoreGridComponent } from 'src/app/shared/grid/core-grid/core-grid.component';
import { GridSettingsService } from 'src/app/shared/grid/core-grid/grid-settings-flyout/grid-settings.service';
import { ISearchFieldData } from 'src/app/shared/grid/baseGrid.component';

@Component({
  standalone: false,
  selector: 'app-job-dictionary',
  templateUrl: './../../shared/grid/core-grid/core-grid.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrls: ['./../../shared/grid/core-grid/core-grid.component.scss', './job-dictionary.component.scss']
})
export class JobDictionaryComponent extends CoreGridComponent<JobDictionary> implements OnInit, AfterViewInit, OnDestroy {

  users: Lookup[] = [];
  private workforceTabsSub: Subscription;

  constructor(
    private jobDictionaryService: JobDictionaryService,
    toastService: ToastService,
    dialog: MatDialog,
    commonService: CommonService,
    authService: AuthService,
    gridViewService: GridViewService,
    gridSettingsService: GridSettingsService,
    private userService: UserService,
    private translateService: TranslateService,
    private jobDateFormatService: DateFormatService,
    private workforceTabsService: WorkforceTabsService,
    protected injector: Injector,
    @Optional() @Inject(MAT_DIALOG_DATA) public searchFieldData: ISearchFieldData
  ) {
    super(toastService, dialog, commonService, authService, gridViewService, gridSettingsService);
    this.gridService = jobDictionaryService;
  }

  protected get objectId(): string {
    return RolePermissionObjects.JobDictionary;
  }

  get hasOrgFilter(): boolean {
    return false;
  }

  protected override get isGridViewEnabled(): boolean { return true; }

  ngOnInit(): void {
    this.searchDialogRef = this.injector.get(MatDialogRef<JobDictionaryComponent>, null);
    super.ngOnInit();
    this.initializeWorkforceTabs();
    this.userService.getAll().subscribe({
      next: (users) => { this.users = users; },
      error: (err) => { this.toastService.showError(err); },
    });
  }

  ngAfterViewInit(): void {
    super.ngAfterViewInit();
    this.loadHeaderData();
  }

  // Resolve and prepend the dynamic Workforce employee-table tab so it stays
  // visible on this page; degrades to the three static tabs if the lookup is
  // empty or errors (DASH-2723).
  private initializeWorkforceTabs(): void {
    this.workforceTabsSub = this.workforceTabsService.getWorkforceTabs().subscribe(tabs => {
      this.tabs = tabs;
      this.selectedTabIndex = tabs.findIndex(
        link => link.objectId === RolePermissionObjects.JobDictionary
      );
    });
  }

  ngOnDestroy(): void {
    this.workforceTabsSub?.unsubscribe();
    // Chain to the base class so CoreGridComponent/BaseGridComponent can clear
    // drill-down overrides and tear down their own subscriptions.
    super.ngOnDestroy();
  }

  override initializeProperties(): void {
    this.addEntityTitle = 'Job Grouping';
    this.searchPlaceholder = 'Search Job Dictionary';
  }

  override initializeColumns(): void {
    this.additionalActions = [
      {
        name: 'View History',
        icon: 'history',
        action: (row: JobDictionary) => this.openHistoryDialog(row)
      }
    ];
    const canConfigure = this.permissionService.hasPermissionFlag(
      RolePermissionObjects.JobDictionary,
      PermissionFlags.UpdateMy | PermissionFlags.UpdateHierarchy
    );
    if (canConfigure) {
      this.additionalButtons = [
        { title: 'Configure Source Field', readonly: false, onClick: () => this.openConfigDialog() }
      ];
    }
    this.columnConfigs = [
      new ColumnConfig<JobDictionary>({
        name: nameOf<JobDictionary>(x => x.code),
        displayName: 'Code',
        cellTemplate: (e) => `<span class="job-code-pill">${escapeHtml(e.code)}</span>`
      }),
      new ColumnConfig<JobDictionary>({
        name: nameOf<JobDictionary>(x => x.name),
        displayName: 'Name',
        cellTemplate: (e) => e.isActive
          ? `${escapeHtml(e.name)}`
          : `${escapeHtml(e.name)} <span class="job-inactive-tag">Inactive</span>`
      }),
      new ColumnConfig<JobDictionary>({
        name: nameOf<JobDictionary>(x => x.description),
        displayName: 'Description'
      }),
      new ColumnConfig<JobDictionary>({
        name: nameOf<JobDictionary>(x => x.isActive),
        displayName: 'Active',
        cellTemplate: (e) => e.isActive
          ? `<span class="job-status-badge active">Active</span>`
          : `<span class="job-status-badge inactive">Inactive</span>`
      }),
      new ColumnConfig<JobDictionary>({
        name: nameOf<JobDictionary>(x => x.isHrisManaged),
        displayName: 'Source',
        cellTemplate: (e) => e.isHrisManaged
          ? `<span class="job-source-badge hris">${escapeHtml(this.translateService.getDynamicTranslation('HRIS'))}</span>`
          : `<span class="job-source-badge manual">${escapeHtml(this.translateService.getDynamicTranslation('Manual'))}</span>`
      }),
      new ColumnConfig<JobDictionary>({
        name: nameOf<JobDictionary>(x => x.createdByUserId),
        displayName: 'Created By',
        cellTemplate: (e) => {
          const userName = this.users?.find(u => u.id.toString() === e.createdByUserId)?.name || '-';
          const date = e.dateCreated
            ? this.jobDateFormatService.convertUTCDateStringToUserTimeZoneString(
                e.dateCreated, this.jobDateFormatService.userDateFormat)
            : '';
          return `<div class="job-created-cell"><span>${escapeHtml(userName)}</span>`
            + `<span class="muted">${escapeHtml(date)}</span></div>`;
        }
      }),
      { name: 'action', displayName: 'Actions' }
    ];
  }

  override objectFilterMap(propertyName: string): any | undefined {
    switch (propertyName) {
      case nameOf<JobDictionary>(x => x.createdByUserId): return this.userService;
      default: return undefined;
    }
  }

  override initializeTabs(): { routerLink: string; title: string; objectId?: string }[] {
    // Static fallback rendered synchronously; the dynamic Workforce tab is
    // prepended in initializeWorkforceTabs() once the lookup resolves.
    return [...workforceRouteConfig];
  }

  override initializeOverviewConfigs(): void {
    this.overviewConfigs = [];
  }

  override getOverviewData(): Observable<void> {
    return of(undefined);
  }

  override openEntityDialog(id?: string): void {
    const model: IFormDialogData = { id };
    this.openDialog(JobDictionaryDialogComponent, model);
  }

  override openEntityDeleteDialog(entity: JobDictionary): void {
    this.openDeleteDialog(DeleteDialog, entity, this.jobDictionaryService, 'Job Grouping');
  }

  private openConfigDialog(): void {
    this.dialog.open(JobDictionaryConfigDialogComponent, {
      width: '520px',
      panelClass: 'job-dictionary-config-dialog'
    });
  }

  private openHistoryDialog(entity: JobDictionary): void {
    this.dialog.open(JobDictionaryHistoryDialogComponent, {
      data: { id: entity.id, title: entity.name },
      width: '900px',
      panelClass: 'history-dialog'
    });
  }
}
